/**
 * Mochi's live-file agent (spec §5.3) — the assistant service's `/live/…`
 * endpoints. A *run* is one request to Mochi to change an open Write
 * document, Grid spreadsheet or Present deck; Mochi edits the live file as
 * the person, after saving a "Before Mochi…" version, and the whole run can
 * be undone in one click.
 */
import { getValidAccessTokenFromStorage } from "@weco/suite-client";

const BASE = "/api/assistant";

export type LiveKind = "doc" | "sheet" | "deck";
export type LiveRunStatus = "queued" | "working" | "done" | "failed" | "undone";

/** Mochi's plan for a new file, published on the run as it builds (create runs only). */
export type LivePlan = {
  stage: "planning" | "writing" | "polishing" | "done" | "failed";
  template: {
    mode: "use" | "adapt" | "create";
    name: string;
    reason: string;
    changes: string;
  } | null;
  parts: {
    id: string;
    heading: string;
    tier: "fast" | "standard" | "strong";
    state: "waiting" | "writing" | "done" | "failed";
  }[];
  polish: "waiting" | "running" | "done" | null;
};

export interface LiveRun {
  id: string;
  file_id: string;
  kind: LiveKind;
  run_kind: "create" | "chat" | "comment";
  by: { id: string; name: string };
  request: string;
  status: LiveRunStatus;
  activity: string | null;
  /** Narration so far (what Mochi said while working). */
  text: string;
  summary: string;
  error: string | null;
  changes: number;
  undoable: boolean;
  created_at: string | null;
  finished_at: string | null;
  plan?: LivePlan | null;
}

export interface LiveChatMessage {
  role: "user" | "mochi";
  text: string;
  file_ids?: string[];
}

/** What "this" means: the selection or a passage (spec `RunFocus`). */
export interface LiveFocus {
  slide_id?: string | number | null;
  element_ids?: string[];
  quote?: string | null;
  blocks?: { from: number; to: number } | null;
}

export class LiveApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
  ) {
    super(message);
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getValidAccessTokenFromStorage();
  let timeZone = "";
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    /* no Intl time zone: the server uses UTC */
  }
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(timeZone ? { "X-Timezone": timeZone } : {}),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let message = `${res.status}`;
    let code = "error";
    try {
      const body = await res.json();
      message = body?.error?.message || body?.detail || message;
      code = body?.error?.code || code;
    } catch {
      /* not JSON */
    }
    throw new LiveApiError(message, res.status, code);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export const liveApi = {
  listRuns: (fileId: string) =>
    req<{ active: LiveRun | null; runs: LiveRun[] }>(`/live/files/${fileId}/runs/`),
  startRun: (fileId: string, body: { messages: LiveChatMessage[]; focus?: LiveFocus; exact?: boolean; person_name?: string }) =>
    req<{ run: LiveRun }>(`/live/files/${fileId}/runs/`, { method: "POST", body: JSON.stringify(body) }),
  getRun: (runId: string) => req<{ run: LiveRun }>(`/live/runs/${runId}/`),
  undo: (runId: string) => req<{ run: LiveRun }>(`/live/runs/${runId}/undo/`, { method: "POST" }),
  generate: (body: {
    kind: "presentation" | "document" | "spreadsheet";
    messages: LiveChatMessage[];
    exact?: boolean;
    title?: string;
    person_name?: string;
  }) => req<{ file_id: string; run: LiveRun }>(`/live/generate/`, { method: "POST", body: JSON.stringify(body) }),
};

export const isActive = (run: LiveRun | null | undefined) =>
  !!run && (run.status === "queued" || run.status === "working");
